import { Component, OnInit, OnDestroy, Input } from "@angular/core";
import moment from "moment";
import { EdpModalService } from "@edp-components/edp-modal";
import { Subject, Observable } from "rxjs";
import { PaymentStatus, PaymentResult } from "@edp-models/edp-payment.model";
import { TranslateService } from "@ngx-translate/core";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import { GoogleAnalyticsService } from "@edp-services/google-analytics.service";
import { Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { GetBalance, SetRecaptcha } from "@edp-store/actions/dashboard.actions";
import { Router } from "@angular/router";

enum PaymentLogStatus {
    MBWAY_CLOSE_TAB = "MBWAY_CLOSE_TAB",
    MBWAY_EXPIRE_TIME = "MBWAY_EXPIRE_TIME",
    MBWAY_SUCCESS = "MBWAY_SUCCESS",
    OPENBANKING_CLOSE_TAB = "OPENBANKING_CLOSE_TAB",
    OPENBANKING_EXPIRE_TIME = "OPENBANKING_EXPIRE_TIME",
    OPENBANKING_SUCCESS = "OPENBANKING_SUCCESS"
}

enum PaymentType {
    MBWAY = "MBWAY",
    OPENBANKING = "OPENBANKING"
}

@Component({
    selector: "edp-mbway-payments",
    templateUrl: "./edp-mbway-payments.component.html",
    styleUrls: ["./edp-mbway-payments.component.scss"]
})
export class EdpMbwayPaymentsComponent implements OnInit, OnDestroy {
    @Input() data: Observable<PaymentResult>;
    public paymentStatus: string;
    public progress = 0;
    public title: string;
    public showCancelOption = true;
    public actionIcon: string;
    public statusClass: string;
    public infoMsg: string;
    public infoReopenUrl: string;
    public paymentType: PaymentType;
    public payment: PaymentResult;
    public showOpenBankingErrorUserReason = false;
    public showBankUrl = false;

    private counterInitialTime = 5 * 60000; // transform minutes into milliseconds
    private counterInterval = null;
    private unsubscribe$: Subject<void> = new Subject();

    constructor(
        private edpModalService: EdpModalService,
        private translate: TranslateService,
        private operationHistoryService: OperationHistoryService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private store: Store<IAppState>,
        private router: Router
    ) { }

    ngOnInit() {
        if (this.data) {
            this.data.subscribe((state: PaymentResult) => {
                this.payment = state;
                this.paymentType = state.phone ? PaymentType.MBWAY : PaymentType.OPENBANKING;
                if (this.paymentType === PaymentType.OPENBANKING) {
                    this.counterInitialTime = 20 * 60000; // transform minutes into milliseconds
                }
                this.updateStatus();
            });

            this.resumeCounter();
        }
    }

    public closeModal(type?): void {
        if (this.paymentType === PaymentType.MBWAY) {
            this.logOperation(PaymentLogStatus.MBWAY_CLOSE_TAB);
        } else {
            this.logOperation(PaymentLogStatus.OPENBANKING_CLOSE_TAB);
        }

        if (this.payment.status === PaymentStatus.PAYMENT_STATUS_SUCCESS) {
            this.store.dispatch(new GetBalance(this.payment.contract));
        }

        this.edpModalService.close(false);

        if (this.payment.status === PaymentStatus.PAYMENT_STATUS_SUCCESS && !!type && type === "payments") {
            this.router.navigateByUrl(`energia/${this.payment.contract.ContractId}/pagamentos-faturas`);
        }
    }

    private updateStatus() {
        switch (this.payment.status) {
            case PaymentStatus.PAYMENT_STATUS_CANCELLED:
            case PaymentStatus.PAYMENT_STATUS_EXPIRED:
            case PaymentStatus.PAYMENT_STATUS_REJECTED:
                if (this.paymentType === PaymentType.OPENBANKING) {
                    this.showOpenBankingErrorUserReason = true;
                }
                this.processError();
                break;
            case PaymentStatus.PAYMENT_STATUS_SUCCESS:
                this.processCompleted();
                break;
            default:
                this.processPending();
                break;
        }
    }

    private processPending() {
        this.paymentStatus = "pt.edp.edponline.payment.modal.payment.status.pending";
        this.actionIcon = "icon-edponline-Bop-132"; // pending
        this.statusClass = "pending";
        if (this.paymentType === PaymentType.MBWAY) {
            this.infoMsg = this.translate.instant("pt.edp.edponline.mbwaypayment.pending.message");
        } else {
            this.infoMsg = this.translate.instant("pt.edp.edponline.payment.modal.pending.bank.message");
            const msgUrl = this.translate.instant("pt.edp.edponline.payment.modal.pending.bank.reopen.url");
            const msgUrlLink = this.translate.instant("pt.edp.edponline.payment.modal.pending.bank.reopen.url.link");
            this.infoReopenUrl = msgUrl + ` <a target='_blank' href="${this.payment.redirectUrl}">${msgUrlLink}</a>`;
        }
    }

    private processError() {
        this.stopAll();
        this.paymentStatus = "pt.edp.edponline.payment.modal.payment.error.title";
        this.actionIcon = "icon-edponline-Bop-025"; // error
        this.statusClass = "error";
        if (this.paymentType === PaymentType.MBWAY) {
            this.infoMsg = this.translate.instant("pt.edp.edponline.mbwaypayment.error.message");
        } else {
            this.infoMsg = this.translate.instant("pt.edp.edponline.payment.modal.error.bank.message");
        }
    }

    private processCompleted() {
        this.stopAll();
        this.paymentStatus = "pt.edp.edponline.payment.modal.payment.completed.title";
        this.actionIcon = "icon-edponline-Bra-072"; // success
        this.statusClass = "success";
        this.infoMsg = this.translate.instant("pt.edp.edponline.mbwaypayment.completed.message");
        if (this.paymentType === PaymentType.MBWAY) {
            this.logOperation(PaymentLogStatus.MBWAY_SUCCESS);
        } else {
            this.logOperation(PaymentLogStatus.OPENBANKING_SUCCESS);
        }
    }

    private stopCounter() {
        clearInterval(this.counterInterval);
    }

    private stopAll() {
        this.stopCounter();
        this.unsubscribe$.next();
        this.store.dispatch(new SetRecaptcha(false));
        this.unsubscribe$.complete();
    }

    private startCounter(timeInMilliseconds: number, progress = 1) {
        const counterTimer = moment.duration(timeInMilliseconds); // init time
        const decreaser = moment.duration(1000); // amount in milliseconds to subtract each second
        const percent = 1000 * 100 / (this.counterInitialTime); // % of progress to increase each second
        this.title = this.setCounterTitle(counterTimer);
        this.progress = progress * percent;
        const getDownloadProgress = () => {
            if (counterTimer.asMilliseconds() > 0) {
                counterTimer.subtract(decreaser);
                this.title = this.setCounterTitle(counterTimer);
                this.progress += percent;
            } else {
                if (this.paymentType === PaymentType.MBWAY) {
                    this.logOperation(PaymentLogStatus.MBWAY_EXPIRE_TIME);
                } else {
                    this.logOperation(PaymentLogStatus.OPENBANKING_EXPIRE_TIME);
                }
                this.processError();
            }
        };

        this.counterInterval = setInterval(getDownloadProgress, 1000);
    }

    private resumeCounter() {
        if (this.payment.isPending()) {
            const startDate = this.payment.requestDate; // get initial date
            const diffTime = moment(moment().toISOString()).diff(startDate); // milliseconds to now from initial date
            const timeLeft = this.counterInitialTime - diffTime; // time left in milliseconds
            const progress = diffTime / 1000; // recalculate progress
            this.startCounter(timeLeft, progress);
        } else {
            this.processError();
        }
    }

    /**
     * Sets counter title
     * @param time (moment.Duration in milliseconds)
     * @returns counter title
     */
    private setCounterTitle(time): string {
        let minutes = time.minutes().toString();
        minutes = (minutes.length > 1) ? minutes : "0" + minutes;

        let seconds = time.seconds().toString();
        seconds = (seconds.length > 1) ? seconds : "0" + seconds;

        return `${minutes} : ${seconds}`;
    }

    ngOnDestroy(): void {
        this.stopAll();
    }

    private logOperation(type: PaymentLogStatus) {
        let value;
        const valueAux = {
            AccountNumber: this.payment.contract.AccountNumber,
            Date: moment().format("YYYY-MM-DD HH:mm:ss")
        };

        if (this.paymentType === PaymentType.MBWAY) {
            value = { ...valueAux, Phone: this.payment.phone };
        } else {
            value = { ...valueAux, Bank: this.payment.bankIdentifier };
        }

        this.operationHistoryService.registerWithContract(
            this.payment.contract.ContractId,
            type,
            value
        );
        this.googleAnalyticsService.datalayerEvent(type);
    }

}
